import type { SessionUser } from '@helpdesk/shared';
import { ApiRequestError, deleteJson, postJson } from '../api/client.ts';
import { isIos, isStandalone } from '../pwa.ts';

/**
 * Web-Push-Abo (F34). iOS erlaubt Push nur in der installierten App
 * („Teilen → Zum Home-Bildschirm“, ab iOS 16.4).
 *
 * Das Abo im Browser sagt nichts darüber, ob der Server es kennt und wem es dort
 * gehört. Aktiv ist darum nur, was der Server für diese Person bestätigt hat.
 */
export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

type PushUser = Pick<SessionUser, 'id' | 'deviceId'>;
type Confirmed = { userId: string; endpoint: string };

const CONFIRMED_KEY = 'hd.push';

function loadConfirmed(): Confirmed | null {
  try {
    const raw = localStorage.getItem(CONFIRMED_KEY);
    return raw ? (JSON.parse(raw) as Confirmed) : null;
  } catch {
    return null;
  }
}

/** Letzte Antwort des Servers. Gilt ohne Netz weiter und nennt nach einem Abo-Wechsel die alte Adresse. */
let confirmed = loadConfirmed();

function setConfirmed(next: Confirmed | null): void {
  confirmed = next;
  try {
    if (next) localStorage.setItem(CONFIRMED_KEY, JSON.stringify(next));
    else localStorage.removeItem(CONFIRMED_KEY);
  } catch {
    /* privater Modus: dann gilt der Stand nur bis zum Neuladen */
  }
}

/**
 * Was ist auf diesem Gerät gerade möglich?
 * - `ios-install`: iPhone/iPad im Browser – erst zum Home-Bildschirm hinzufügen
 * - `unsupported`: Browser kann kein Push
 * - `denied`: im Browser blockiert – nur in den Einstellungen wieder freizugeben
 * - `available`: Einschalten möglich
 * - `subscribed`: aktiv
 */
export type PushState = 'ios-install' | 'unsupported' | 'denied' | 'available' | 'subscribed';

export async function pushState(user: PushUser, vapidKey: string | null): Promise<PushState> {
  if (isIos() && !isStandalone()) return 'ios-install';
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'available';
  await syncPush(user, vapidKey);
  const sub = await currentSubscription();
  return sub && confirmed?.userId === user.id && confirmed.endpoint === sub.endpoint ? 'subscribed' : 'available';
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Ein Browser ohne Angabe lässt keinen Vergleich zu, neu abonnieren hülfe dort nicht. */
function usesKey(sub: PushSubscription, vapidKey: string): boolean {
  const current = sub.options.applicationServerKey;
  if (!current) return true;
  const wanted = urlBase64ToUint8Array(vapidKey);
  const bytes = new Uint8Array(current);
  return bytes.length === wanted.length && bytes.every((byte, i) => byte === wanted[i]);
}

/** Unter einem alten Schlüssel weist der Push-Dienst jede Sendung ab, und der Browser nimmt einen neuen erst nach dem Lösen an. */
async function subscriptionFor(reg: ServiceWorkerRegistration, vapidKey: string): Promise<PushSubscription> {
  const sub = await reg.pushManager.getSubscription();
  if (sub && usesKey(sub, vapidKey)) return sub;
  await sub?.unsubscribe();
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) });
}

/**
 * Ist die Adresse neu, fällt die alte Zeile beim Server weg. Mit `syncOnly` legt der Server nichts an
 * und lässt eine Fehlerserie stehen. Ergebnis: Kennt der Server die Adresse?
 */
async function register(userId: string, sub: PushSubscription, syncOnly = false): Promise<boolean> {
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  const oldEndpoint = confirmed && confirmed.endpoint !== json.endpoint ? confirmed.endpoint : undefined;
  const { known } = await postJson<{ known: boolean }>('/api/profile/push-subscriptions', {
    endpoint: json.endpoint,
    keys: json.keys,
    oldEndpoint,
    sync: syncOnly,
  });
  if (syncOnly && !known) return false;
  setConfirmed({ userId, endpoint: json.endpoint });
  return true;
}

let sync: { scope: string; done: Promise<void> } | null = null;

/**
 * Eine Adresse, die der Server nicht kennt, wird ersetzt und nie wieder angemeldet: Der Server löscht,
 * was der Push-Dienst abweist, und von hier aus sieht eine tote Adresse aus wie eine lebende.
 */
async function reconcile(userId: string, vapidKey: string): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!reg || !sub) return;
    if (usesKey(sub, vapidKey) && (await register(userId, sub, true))) return;
    await sub.unsubscribe();
    await register(userId, await subscriptionFor(reg, vapidKey));
  } catch (err) {
    if (err instanceof ApiRequestError && err.status >= 400 && err.status < 500) setConfirmed(null);
    // Ohne klare Antwort bleibt der letzte Stand, und der nächste Aufruf versucht es wieder.
    else sync = null;
  }
}

/**
 * Gleicht das Abo dieses Browsers mit dem Server ab. Es gehört danach der angemeldeten Person,
 * auch wenn die Sitzung der vorherigen nur abgelaufen ist, und trägt den aktuellen Schlüssel.
 * Holt nach, was dem Service Worker bei `pushsubscriptionchange` misslang, und ersetzt eine
 * Adresse, die der Server verworfen hat. Scheitert nie.
 * Läuft einmal je Person und Schlüssel. Hallen-Tablets abonnieren nichts.
 */
export function syncPush(user: PushUser, vapidKey: string | null): Promise<void> {
  if (user.deviceId || !vapidKey || !pushSupported() || Notification.permission !== 'granted') return Promise.resolve();
  const scope = `${user.id} ${vapidKey}`;
  if (sync?.scope !== scope) sync = { scope, done: reconcile(user.id, vapidKey) };
  return sync.done;
}

export class PushNotReadyError extends Error {
  constructor() {
    super('Der Dienst für Benachrichtigungen ist noch nicht bereit. Bitte die Seite neu laden und es dann erneut versuchen.');
    this.name = 'PushNotReadyError';
  }
}

const WORKER_READY_MS = 10_000;

/** `ready` erfüllt sich nie, wenn kein Service Worker läuft: Registrierung gescheitert, Entwicklungsbetrieb. */
async function readyRegistration(): Promise<ServiceWorkerRegistration> {
  if (!(await navigator.serviceWorker.getRegistration())) throw new PushNotReadyError();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new PushNotReadyError()), WORKER_READY_MS);
  });
  try {
    return await Promise.race([navigator.serviceWorker.ready, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** Fragt die Erlaubnis ab (muss aus einem Klick heraus geschehen) und meldet das Gerät an. */
export async function subscribePush(userId: string, vapidPublicKey: string): Promise<boolean> {
  if (!pushSupported()) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const sub = await subscriptionFor(await readyRegistration(), vapidPublicKey);
  try {
    await register(userId, sub);
  } catch (err) {
    // Sonst bleibt ein Abo im Browser, das der Server nicht kennt.
    await sub.unsubscribe().catch(() => false);
    throw err;
  }
  return true;
}

/**
 * Gerät abmelden. Läuft auch beim Abmelden aus der App: Auf geteilten Tablets
 * sollen die Benachrichtigungen der vorherigen Person nicht weiter erscheinen.
 */
export async function unsubscribePush(): Promise<void> {
  setConfirmed(null);
  const sub = await currentSubscription();
  if (!sub) return;
  await deleteJson('/api/profile/push-subscriptions', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe().catch(() => false);
}
